import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../context/ToastContext";
import { type CatalogAdminView, type StockView, useCatalogMutation } from "../../hooks/catalog";
import { useLocale } from "../../hooks/useLocale";
import { api } from "../../lib/api";
import { localName, money } from "../../lib/format";
import { Empty, Pill, SectionHeader } from "../ui/Badges";
import { Modal } from "../ui/Modal";
import { MoneyInput } from "./fields";

type Action = "intake" | "adjust" | "waste" | "par";
const level = (qty: number, par: number) => (par <= 0 ? "ok" : qty <= par * 0.25 ? "critical" : qty < par ? "low" : "ok");

/** Receive / correct / write off stock, and set the re-order level. */
function StockDialog({ item, onClose }: { item: StockView; onClose: () => void }) {
  const { t } = useTranslation();
  const { toast, toastError } = useToast();
  const [action, setAction] = useState<Action>("intake");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const n = Number(amount);
  const ok = amount.trim() !== "" && Number.isFinite(n) && (action === "par" ? n >= 0 : action === "adjust" ? n !== 0 : n > 0);
  const save = useCatalogMutation(async () => {
    if (action === "intake") return api.post(`/inventory/${item.id}/intake`, { qty: n, note: note.trim() || undefined });
    if (action === "waste") return api.post(`/inventory/${item.id}/adjust`, { delta: -n, reason: "waste", note: note.trim() || undefined });
    if (action === "adjust") return api.post(`/inventory/${item.id}/adjust`, { delta: n, reason: "adjust", note: note.trim() || undefined });
    return api.patch(`/inventory/${item.id}`, { parLevel: n });
  });
  async function submit() {
    try {
      await save.mutateAsync(undefined);
      toast(t("pricing.saved"), "success");
      onClose();
    } catch (e) {
      toastError(e);
    }
  }
  return (
    <Modal open size="sm" onClose={onClose} title={item.name}
      footer={<><button type="button" className="btn-ghost" onClick={onClose}>{t("common.cancel")}</button><button type="button" className="btn-primary" disabled={!ok || save.isPending} onClick={() => void submit()}>{t("pricing.save")}</button></>}>
      <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={t("pricing.stock.action")}>
        {(["intake", "adjust", "waste", "par"] as const).map((a) => (
          <button key={a} type="button" role="radio" aria-checked={action === a} onClick={() => { setAction(a); setAmount(""); }}
            className={`rounded-xl border-2 px-2.5 py-1 text-[11px] font-black ${action === a ? "border-cosmic-purple bg-purple-50 text-cosmic-purple" : "border-gray-200 bg-white text-gray-600"}`}>{t(`pricing.stock.${a}`)}</button>
        ))}
      </div>
      <div>
        <label className="label" htmlFor="sk-amount">{t(`pricing.stock.${action}Label`, { uom: item.uom })}</label>
        <input id="sk-amount" className="field num font-mono" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
      </div>
      {action !== "par" && (
        <div>
          <label className="label" htmlFor="sk-note">{t("pricing.stock.note")}</label>
          <input id="sk-note" className="field" value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} />
        </div>
      )}
    </Modal>
  );
}

function NewItemDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { toast, toastError } = useToast();
  const [f, setF] = useState({ sku: "", name: "", nameAr: "", uom: "each", unitCost: 0, initialQty: "0", parLevel: "0" });
  const save = useCatalogMutation(() => api.post("/catalog-admin/stock-items", { sku: f.sku.trim(), name: f.name.trim(), nameAr: f.nameAr.trim() || null, uom: f.uom, unitCost: f.unitCost, initialQty: Number(f.initialQty) || 0, parLevel: Number(f.parLevel) || 0 }));
  async function submit() {
    try {
      await save.mutateAsync(undefined);
      toast(t("pricing.saved"), "success");
      onClose();
    } catch (e) {
      toastError(e);
    }
  }
  return (
    <Modal open size="md" onClose={onClose} title={t("pricing.stock.newTitle")}
      footer={<><button type="button" className="btn-ghost" onClick={onClose}>{t("common.cancel")}</button><button type="button" className="btn-primary" disabled={!f.sku.trim() || !f.name.trim() || save.isPending} onClick={() => void submit()}>{t("pricing.save")}</button></>}>
      <p className="text-[11px] font-semibold text-gray-500">{t("pricing.stock.newHelp")}</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div><label className="label" htmlFor="ns-name">{t("pricing.product.name")}</label><input id="ns-name" className="field" value={f.name} maxLength={120} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
        <div><label className="label" htmlFor="ns-name-ar">{t("pricing.product.nameAr")}</label><input id="ns-name-ar" className="field" dir="rtl" value={f.nameAr} maxLength={120} onChange={(e) => setF({ ...f, nameAr: e.target.value })} /></div>
        <div><label className="label" htmlFor="ns-sku">{t("pricing.product.sku")}</label><input id="ns-sku" className="field num font-mono" value={f.sku} maxLength={40} onChange={(e) => setF({ ...f, sku: e.target.value.toUpperCase() })} /></div>
        <div>
          <label className="label" htmlFor="ns-uom">{t("pricing.stock.uom")}</label>
          <select id="ns-uom" className="field" value={f.uom} onChange={(e) => setF({ ...f, uom: e.target.value })}>
            {["each", "kg", "litre", "box"].map((u) => <option key={u} value={u}>{t(`pricing.stock.uoms.${u}`)}</option>)}
          </select>
        </div>
        <div><label className="label" htmlFor="ns-cost">{t("pricing.stock.unitCost", { cur: t("currency") })}</label><MoneyInput id="ns-cost" value={f.unitCost} onChange={(v) => setF({ ...f, unitCost: v ?? 0 })} /></div>
        <div><label className="label" htmlFor="ns-qty">{t("pricing.product.openingQty")}</label><input id="ns-qty" className="field num font-mono" inputMode="decimal" value={f.initialQty} onChange={(e) => setF({ ...f, initialQty: e.target.value })} /></div>
        <div><label className="label" htmlFor="ns-par">{t("pricing.product.par")}</label><input id="ns-par" className="field num font-mono" inputMode="decimal" value={f.parLevel} onChange={(e) => setF({ ...f, parLevel: e.target.value })} /></div>
      </div>
    </Modal>
  );
}

export function StockPanel({ data, canEdit, canReceive }: { data: CatalogAdminView; canEdit: boolean; canReceive: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [item, setItem] = useState<StockView | null>(null);
  const [adding, setAdding] = useState(false);
  const list = data.stock.filter((s) => s.active);
  return (
    <section className="card space-y-3 p-4 sm:p-5" aria-label={t("pricing.tabs.stock")}>
      <SectionHeader
        title={`📦 ${t("pricing.tabs.stock")}`}
        subtitle={t("pricing.stock.subtitle")}
        right={canEdit ? <button type="button" className="btn-primary !px-3 !py-1.5 text-xs" onClick={() => setAdding(true)}>＋ {t("pricing.stock.add")}</button> : undefined}
      />
      {list.length === 0 ? (
        <Empty>{t("pricing.stock.none")}</Empty>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-start text-xs">
            <thead className="border-b border-gray-100 bg-gray-50 text-[10px] font-black uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-3 py-2 text-start">{t("pricing.product.name")}</th>
                <th className="px-2 py-2 text-end">{t("pricing.stock.onHand")}</th>
                <th className="px-2 py-2 text-end">{t("pricing.product.par")}</th>
                <th className="px-2 py-2 text-end">{t("pricing.stock.unitCostShort")}</th>
                <th className="px-2 py-2 text-center">{t("pricing.product.status")}</th>
                <th className="px-3 py-2 text-end" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 font-bold text-gray-700">
              {list.map((s) => {
                const lv = s.stocked ? level(s.qtyOnHand ?? 0, s.parLevel ?? 0) : null;
                return (
                  <tr key={s.id} data-sku={s.sku}>
                    <td className="px-3 py-2.5"><div className="font-extrabold text-cosmic-navy">{localName(s, locale)}</div><div className="num font-mono text-[10px] text-gray-400">{s.sku} · {t(`pricing.stock.uoms.${s.uom}`)}</div></td>
                    <td className="num px-2 py-2.5 text-end font-mono">{s.stocked ? s.qtyOnHand : "—"}</td>
                    <td className="num px-2 py-2.5 text-end font-mono">{s.stocked ? s.parLevel : "—"}</td>
                    <td className="num px-2 py-2.5 text-end font-mono">{money(s.unitCost)}</td>
                    <td className="px-2 py-2.5 text-center">{lv && <Pill tone={lv === "ok" ? "green" : lv === "low" ? "amber" : "red"}>{t(`inventory.level.${lv}`)}</Pill>}</td>
                    <td className="px-3 py-2.5 text-end">{canReceive && <button type="button" className="btn-ghost !px-2.5 !py-1 text-xs" onClick={() => setItem(s)}>📥 {t("pricing.stock.manage")}</button>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {item && <StockDialog item={item} onClose={() => setItem(null)} />}
      {adding && <NewItemDialog onClose={() => setAdding(false)} />}
    </section>
  );
}
